import React from "react";
import { FiCopy, FiShield } from "react-icons/fi";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import {
  errorMessage,
  isPayoutStepUpEnabled,
  pickString,
  stableIdempotencyKey,
  verifyStepUp,
} from "./api/payoutMutationHelpers";
import { readBackupCodes, readOtpAuthUri } from "./StepUpChallengeModal";

type MfaStatus = {
  enrolled: boolean;
  preferred_channel: string;
  masked_mobile: string;
  backup_codes_remaining: number;
};

function PayoutMfaSetup() {
  const [status, setStatus] = React.useState<MfaStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [enrollUri, setEnrollUri] = React.useState("");
  const [enrollSecret, setEnrollSecret] = React.useState("");
  const [confirmCode, setConfirmCode] = React.useState("");
  const [backupCodes, setBackupCodes] = React.useState<string[]>([]);
  const [busy, setBusy] = React.useState(false);

  const refreshStatus = React.useCallback(async () => {
    if (!isPayoutApiMode()) {
      setStatus({
        enrolled: false,
        preferred_channel: "SMS",
        masked_mobile: "",
        backup_codes_remaining: 0,
      });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const payload = await payoutApi.mfaStatus();
      setStatus({
        enrolled: Boolean(payload.enrolled),
        preferred_channel: pickString(payload, ["preferred_channel", "preferredChannel"], "SMS"),
        masked_mobile: pickString(payload, ["masked_mobile", "maskedMobile"], ""),
        backup_codes_remaining: Number(
          payload.backup_codes_remaining ?? payload.backupCodesRemaining ?? 0,
        ),
      });
    } catch (err) {
      setError(errorMessage(err, "Unable to load MFA status."));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  const startEnroll = async () => {
    setBusy(true);
    setError("");
    setNotice("");
    setBackupCodes([]);
    try {
      const payload = await payoutApi.mfaTotpEnroll(
        stableIdempotencyKey("mfa-totp-enroll", "current"),
      );
      setEnrollUri(readOtpAuthUri(payload));
      setEnrollSecret(pickString(payload, ["secret"], ""));
      setNotice("Scan the URI in Google Authenticator, then enter the 6-digit code to confirm.");
    } catch (err) {
      setError(errorMessage(err, "Unable to start authenticator enrollment."));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    if (confirmCode.trim().length !== 6) {
      setError("Enter the 6-digit authenticator code.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = await payoutApi.mfaTotpConfirm(
        { code: confirmCode.trim() },
        stableIdempotencyKey("mfa-totp-confirm", confirmCode.trim()),
      );
      setBackupCodes(readBackupCodes(payload));
      setEnrollUri("");
      setEnrollSecret("");
      setConfirmCode("");
      setNotice("Google Authenticator is now enabled for step-up verification.");
      await refreshStatus();
    } catch (err) {
      setError(errorMessage(err, "Invalid code. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const disableTotp = async () => {
    if (!isPayoutStepUpEnabled()) {
      setError("Step-up must be enabled to disable authenticator.");
      return;
    }
    const code = window.prompt("Enter your 6-digit authenticator code to disable TOTP:");
    if (!code?.trim()) return;
    setBusy(true);
    setError("");
    try {
      const stepUpToken = await verifyStepUp({
        purpose: "DISABLE_MFA",
        method: "authenticator",
        code: code.trim(),
      });
      await payoutApi.mfaTotpDisable(
        stableIdempotencyKey("mfa-totp-disable", "current"),
        stepUpToken,
      );
      setNotice("Authenticator disabled. SMS fallback applies when enabled.");
      await refreshStatus();
    } catch (err) {
      setError(errorMessage(err, "Unable to disable authenticator."));
    } finally {
      setBusy(false);
    }
  };

  const regenerateBackupCodes = async () => {
    const code = window.prompt("Enter your 6-digit authenticator code:");
    if (!code?.trim()) return;
    setBusy(true);
    setError("");
    try {
      const stepUpToken = await verifyStepUp({
        purpose: "REGENERATE_MFA_BACKUP",
        method: "authenticator",
        code: code.trim(),
      });
      const payload = await payoutApi.mfaRegenerateBackupCodes(
        stableIdempotencyKey("mfa-backup-regenerate", "current"),
        stepUpToken,
      );
      setBackupCodes(readBackupCodes(payload));
      setNotice("New backup codes generated. Store them securely.");
      await refreshStatus();
    } catch (err) {
      setError(errorMessage(err, "Unable to regenerate backup codes."));
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Copied to clipboard.");
    } catch {
      setError("Copy failed — select and copy manually.");
    }
  };

  return (
    <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
      <div className="mb-4 flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-[8px] bg-[#1B163B0D] text-lg text-[#1B163B]">
          <FiShield />
        </span>
        <div>
          <h2 className="text-lg font-semibold text-[#1B163B]">Google Authenticator</h2>
          <p className="text-sm font-medium text-[#505E8E]">
            Enroll TOTP for step-up on payouts, approvals, and admin actions.
          </p>
        </div>
      </div>

      {loading ? (
        <p className="text-sm font-medium text-[#505E8E]">Loading MFA status…</p>
      ) : (
        <div className="space-y-3 text-sm font-medium text-[#505E8E]">
          <p>
            Status:{" "}
            <span className="font-semibold text-[#1B163B]">
              {status?.enrolled ? "Enrolled" : "Not enrolled"}
            </span>
          </p>
          {status?.masked_mobile ? <p>SMS fallback mobile: {status.masked_mobile}</p> : null}
          {status?.enrolled ? (
            <p>Backup codes remaining: {status.backup_codes_remaining}</p>
          ) : null}
        </div>
      )}

      {error ? <p className="mt-3 text-sm font-semibold text-[#B42318]">{error}</p> : null}
      {notice ? <p className="mt-3 text-sm font-semibold text-[#166534]">{notice}</p> : null}

      {!status?.enrolled ? (
        <div className="mt-4 space-y-3">
          <button
            type="button"
            disabled={busy || !isPayoutApiMode()}
            onClick={startEnroll}
            className="h-10 rounded-[6px] bg-[#1B163B] px-4 text-sm font-semibold text-white disabled:opacity-50"
          >
            Start enrollment
          </button>
          {enrollUri ? (
            <div className="rounded-[6px] border border-[#E0E5F2] bg-[#F8FAFC] p-3">
              <p className="text-xs font-semibold text-[#505E8E]">otpauth URI</p>
              <p className="mt-2 break-all text-xs font-medium text-[#1B163B]">{enrollUri}</p>
              <div className="mt-2 flex gap-2">
                <button
                  type="button"
                  onClick={() => copyText(enrollUri)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#6687FF]"
                >
                  <FiCopy /> Copy URI
                </button>
                {enrollSecret ? (
                  <button
                    type="button"
                    onClick={() => copyText(enrollSecret)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-[#6687FF]"
                  >
                    <FiCopy /> Copy secret
                  </button>
                ) : null}
              </div>
              <label className="mt-4 block">
                <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
                  Confirmation code
                </span>
                <input
                  value={confirmCode}
                  onChange={(event) =>
                    setConfirmCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  inputMode="numeric"
                  className="h-10 w-full max-w-[220px] rounded-[6px] border border-[#E0E5F2] px-3 text-sm font-semibold text-[#1B163B]"
                  placeholder="6-digit code"
                />
              </label>
              <button
                type="button"
                disabled={busy || confirmCode.length !== 6}
                onClick={confirmEnroll}
                className="mt-3 h-10 rounded-[6px] bg-[#1B163B] px-4 text-sm font-semibold text-white disabled:opacity-50"
              >
                Confirm enrollment
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={busy}
            onClick={regenerateBackupCodes}
            className="h-10 rounded-[6px] border border-[#E0E5F2] px-4 text-sm font-semibold text-[#1B163B]"
          >
            Regenerate backup codes
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={disableTotp}
            className="h-10 rounded-[6px] border border-[#FECACA] px-4 text-sm font-semibold text-[#B42318]"
          >
            Disable authenticator
          </button>
        </div>
      )}

      {backupCodes.length ? (
        <div className="mt-4 rounded-[6px] border border-[#BBF7D0] bg-[#F0FDF4] p-3">
          <p className="text-xs font-semibold text-[#166534]">Backup codes (show once)</p>
          <ul className="mt-2 grid gap-1 text-sm font-semibold text-[#1B163B] md:grid-cols-2">
            {backupCodes.map((code) => (
              <li key={code}>{code}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default PayoutMfaSetup;
